import React, { useState, useEffect } from 'react';
import { Card, Select, FormLayout, Popover, Button, TextField, Text, InlineStack } from '@shopify/polaris';
import { LineChart, Line, ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import { formatDate, formatToCurrency } from '../../../../shared/components/functions';
import { useToggle } from '../../../../shared/useToogle';
import { CardHeading } from '../../../../shared/components/cards/CardHeading';
import { formatAnalyticsPeriodLabel } from './periodFormatting';

export default function BrandTotalSales() {
  const [data, setData] = useState([]);
  const [groupBy, setGroupBy] = useState('YEAR');
  const [popoverActive, setPopoverActive] = useToggle();
  const [startDate, setStartDate] = useState(formatDate('1996-01-01'));
  const [endDate, setEndDate] = useState(formatDate(new Date()));
  const [selectedSupplier, setSelectedSupplier] = useState('');

  const suppliers = [
    { label: 'All Suppliers', value: '' },
    { label: 'Dalla Corte', value: '1156325961' },
    { label: 'Aha Innovations', value: '2147421779' },
    { label: 'Mahlkonig (combined)', value: '3093,-1336183844' },
    { label: 'PuqPress', value: '2147422657' },
    { label: 'Bwt', value: '1196063953' },
    { label: 'Langguth', value: '1838' },
    { label: 'Nerak', value: '1281109380' },
    { label: 'ICA', value: '2475' },
    { label: 'All Suppliers', value: '' }
  ];

  const activator = (
    <Button onClick={setPopoverActive} disclosure>
      Customize
    </Button>
  );

  const options = [
    { label: 'Month', value: 'MONTH' },
    { label: 'Year', value: 'YEAR' },
    { label: 'Day', value: 'DAY' },
    { label: 'Quarter', value: 'QUARTER' }
  ];

  async function fetchData() {
    const params = new URLSearchParams({
      groupBy: groupBy,
      startDate: startDate,
      endDate: endDate
    });

    if (selectedSupplier) {
      params.append('supplierId', selectedSupplier);
    }

    const response = await fetch(`/api/metrics/supplier-sales?${params}`);
    const data = await response.json();
    setData(
      data.map((point) => {
        return {
          name: point.period,
          ...point
        };
      })
    );
  }

  useEffect(() => {
    fetchData();
  }, [groupBy, startDate, endDate, selectedSupplier]);

  const customize_graph = (
    <Popover active={popoverActive} activator={activator} onClose={setPopoverActive} ariaHaspopup={false} sectioned>
      <FormLayout>
        <Select label="Group by" options={options} onChange={setGroupBy} value={groupBy} />
        <Select label="Supplier" options={suppliers} onChange={setSelectedSupplier} value={selectedSupplier} />
        <TextField id="start_date" maxLength={60} type="date" autoComplete="off" label="Start Date" value={startDate} onChange={setStartDate} />
        <TextField id="end_date" maxLength={60} type="date" autoComplete="off" label="End Date" value={endDate} onChange={setEndDate} />
      </FormLayout>
    </Popover>
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Card>
          <Text className="label">{`Period : ${formatAnalyticsPeriodLabel(label)}`}</Text>
          <p className="label">{`Total Sales : ${formatToCurrency(payload[0] ? payload[0].value : 0)}`}</p>
        </Card>
      );
    }
    return null;
  };

  // Get the selected supplier label for display
  const getSelectedSupplierLabel = () => {
    if (!selectedSupplier) return 'All Suppliers';
    const supplier = suppliers.find((s) => s.value === selectedSupplier);
    return supplier ? supplier.label : 'All Suppliers';
  };

  return (
    <Card>
      <InlineStack align="space-between">
        <div>
          <CardHeading title={`Supplier Sales Metrics - ${getSelectedSupplierLabel()}`} />
          <Text tone="subdued" as="p">
            Sum of sales order values for the selected period VAT excluded. Canceled orders not included.
          </Text>
        </div>
        {customize_graph}
      </InlineStack>

      <div style={{ width: '100%', height: '650px', marginTop: '20px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            width={1500}
            height={300}
            data={data}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5
            }}
          >
            <Legend />
            <CartesianGrid strokeDasharray="1 5" />
            <XAxis dataKey="period" tickFormatter={formatAnalyticsPeriodLabel} />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Line type="monotone" dataKey="total_sales" stroke="#64BE7B" activeDot={{ r: 8 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
